/* eslint-disable @typescript-eslint/naming-convention */
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Menu,
  Stack,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import useJDialog from '@/hooks/useJDialog';
import { JButton } from '@atoms/JButton';
import { UseMutateFunction } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import IUserListItem from '../../models/user-list-item';
import { useDisableUser, useEnableUser } from '../../mutations/userMutations';
import useJSnackbar from '@/hooks/useJSnackbar';
import { useNavigate } from 'react-router-dom';
import Paths from '@/routes/paths';
import { StatusChip } from '@atoms/StatusChip';
import { TableRowStyled } from '@styled/table-row.styled';
import JTableCell from '@atoms/JTabelCell';
import { ShowMoreButton } from '@atoms/ShowMoreButton';
import { MenuItemStyled } from '@styled/menu-item.styled';
import { DialogContentStyled } from '@styled/dialog-content-styled';

interface Props {
  row: IUserListItem;
}

type disbaleMutationType = UseMutateFunction<
  AxiosResponse<null, any>,
  unknown,
  { id: string },
  unknown
>;

const UserRow = ({ row }: Props) => {
  const { t } = useTranslation();

  const {
    enterprise_name,
    establishmentscount,
    user_email,
    user_firstname,
    user_id,
    user_lastname,
    user_mobile_phone,
    deleted_at,
  } = row;

  const [rowMenuanchorEl, setRowMenuAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [isActive, setIsActive] = useState<boolean>();
  const navigation = useNavigate();
  const open = Boolean(rowMenuanchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setRowMenuAnchorEl(event.currentTarget);
  };
  const handleRowMenuClose = () => {
    setRowMenuAnchorEl(null);
  };

  const {
    mutate: enableUserMutate,
    isSuccess: enableSuccess,
    isError: enableError,
  } = useEnableUser();
  const {
    mutate: disableUserMutate,
    isSuccess: disableSuccess,
    isError: disableError,
  } = useDisableUser();

  const { showJDialog, closeJDialog } = useJDialog();
  const { showSnackbar } = useJSnackbar();

  const enableUser = (id: string) => {
    handleRowMenuClose();
    enableUserMutate({ id });
    handleRowMenuClose();
  };

  const openDisableDialog = (
    userName: string,

    enterpriseId: string,
    disableMutation: disbaleMutationType,
  ): void => {
    handleRowMenuClose();
    showJDialog(
      'Désactiver cet utilisateur ?',
      <DisableDialogContent
        enterpriseId={enterpriseId}
        userName={userName}
        disableMutation={disableMutation}
      />,
    );
  };
  useEffect(() => {
    setIsActive(!deleted_at);
  }, [deleted_at]);
  useEffect(() => {
    if (!enableSuccess) return;
    setIsActive(enableSuccess);
  }, [enableSuccess]);
  useEffect(() => {
    if (!disableSuccess) return;
    setIsActive(!disableSuccess);
    closeJDialog();
  }, [disableSuccess]);

  useEffect(() => {
    if (enableError) showSnackbar(t('API_ERROR'), 'error');
  }, [enableError]);
  useEffect(() => {
    if (disableError) showSnackbar(t('API_ERROR'), 'error');
  }, [disableError]);
  return (
    <TableRowStyled key={user_id} data-test={`user-list-row-${user_email}`}>
      <JTableCell isbold>{user_firstname}</JTableCell>
      <JTableCell>{user_lastname}</JTableCell>
      <JTableCell>{enterprise_name}</JTableCell>
      <JTableCell>{user_email}</JTableCell>
      <JTableCell>{user_mobile_phone}</JTableCell>
      <JTableCell>
        <StatusChip
          label={isActive ? t('ACTIVE') : t('INACTIVE')}
          variant={isActive ? 'success' : 'danger'}
        ></StatusChip>
      </JTableCell>
      <JTableCell>{establishmentscount}</JTableCell>
      <JTableCell>
        <ShowMoreButton onClick={handleClick} open={open} />
        <Menu
          id="basic-menu"
          anchorEl={rowMenuanchorEl}
          open={open}
          onClose={handleRowMenuClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          <MenuItemStyled
            data-qa="action"
            red={!!isActive}
            green={!isActive}
            onClick={() =>
              isActive
                ? openDisableDialog(
                    `${row.user_firstname} ${row.user_lastname}`,
                    user_id,
                    disableUserMutate,
                  )
                : enableUser(user_id)
            }
          >
            {isActive ? t('DESACTIVATE') : t('ACTIVATE')}
          </MenuItemStyled>
          {isActive && (
            <MenuItemStyled
              data-qa="navigate"
              red={false}
              green={false}
              onClick={() => navigation(Paths.EditUser.replace(':id', user_id))}
            >
              {t('USER_DETAILS')}
            </MenuItemStyled>
          )}
        </Menu>
      </JTableCell>
    </TableRowStyled>
  );
};

const DisableDialogContent = ({
  userName,
  enterpriseId,
  disableMutation,
}: {
  userName: string;
  enterpriseId: string;
  disableMutation: disbaleMutationType;
}) => {
  const { closeJDialog } = useJDialog();
  const { t } = useTranslation();
  return (
    <Stack gap={2}>
      <DialogContentStyled>
        {t('DISABLE_USER_DIALOG_TEXT')}
        <strong> {userName}</strong> ?
      </DialogContentStyled>
      <FormGroup>
        <FormControlLabel
          control={<Checkbox />}
          label={t('DISABLE_USER_OPTION_1')}
        />
      </FormGroup>
      <Stack direction={'row'} gap={1}>
        <JButton
          data-qa="disable"
          onClick={() => disableMutation({ id: enterpriseId })}
          variant="contained"
          color="error"
        >
          {t('DISABLE_USER_DISABLE_BUTTON')}
        </JButton>
        <JButton
          data-qa="close"
          onClick={closeJDialog}
          variant="contained"
          color="info"
        >
          Annuler
        </JButton>
      </Stack>
    </Stack>
  );
};

export default UserRow;
